import { useCallback, useEffect, useState } from "react";
import { useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { invocarFuncao } from "@/lib/edgeFunction";

/**
 * Portal do corretor da Azos com o login da corretora (migration 288, edge
 * function seguradoras-azos-portal). A senha vai só para a function, que guarda
 * o token de renovação no Vault e lê comissões, recebimentos e campanhas.
 * Sem conexão, a Azos continua funcionando pela conta de plataforma da North.
 */

export type LinhaAzosPortal = {
  situacao: "conectado" | "erro" | "login_expirado";
  email: string;
  corretora: string | null;
  erro: string | null;
  lido_em: string | null;
};

const quando = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : null;

/** Estado e texto do cartão a partir da linha de azos_portal_conexoes. */
export const resumoAzosPortal = (l: LinhaAzosPortal | null | undefined) => {
  if (!l) return { estado: "nao_conectado" as const, detalhe: "Cotação e apólices pela corretora da North." };
  if (l.situacao === "login_expirado") return { estado: "incompleto" as const, detalhe: "Login expirado. Entrar de novo." };
  if (l.situacao === "erro") return { estado: "incompleto" as const, detalhe: l.erro ?? "Não foi possível ler o portal." };
  const lido = quando(l.lido_em);
  return {
    estado: "conectado" as const,
    detalhe: [l.corretora ?? l.email, lido ? `lido em ${lido}` : null].filter(Boolean).join(" · "),
  };
};

/** A linha do usuário (RLS: a própria; super admin vê todas). */
export const useConexaoAzosPortal = (crmUserId: number | string | null | undefined) => {
  const [linha, setLinha] = useState<LinhaAzosPortal | null>(null);
  const recarregar = useCallback(async () => {
    if (crmUserId == null) return null;
    const { data } = await supabase
      .from("azos_portal_conexoes" as never)
      .select("situacao, email, corretora, erro, lido_em")
      .eq("crm_user_id", Number(crmUserId))
      .maybeSingle();
    const l = (data ?? null) as LinhaAzosPortal | null;
    setLinha(l);
    return l;
  }, [crmUserId]);
  useEffect(() => {
    void recarregar();
  }, [recarregar]);
  return { linha, recarregar };
};

export const desconectarAzosPortal = async () => {
  const { error } = await supabase.rpc("azos_portal_desconectar" as never);
  if (error) throw error;
};

/** Leitura sob demanda (botão Atualizar das telas Azos e Recebimentos). */
export const lerAzosPortal = () =>
  invocarFuncao<{ ok: boolean }>("seguradoras-azos-portal", { body: { acao: "ler" }, quando: "ler o portal da Azos" });

/** Diálogo de login: e-mail e senha do portal do corretor. */
export const DialogoAzosPortal = ({
  aberto,
  emailAtual,
  aoFechar,
  recarregar,
}: {
  aberto: boolean;
  emailAtual?: string | null;
  aoFechar: () => void;
  recarregar: () => Promise<LinhaAzosPortal | null>;
}) => {
  const notify = useNotify();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) {
      setEmail(emailAtual ?? "");
      setSenha("");
      setErro(null);
    }
  }, [aberto, emailAtual]);

  const conectar = async () => {
    setEnviando(true);
    setErro(null);
    const { erro: falha } = await invocarFuncao<{ ok: boolean }>("seguradoras-azos-portal", {
      body: { acao: "conectar", email: email.trim(), senha },
      quando: "conectar ao portal da Azos",
    });
    setEnviando(false);
    setSenha("");
    await recarregar();
    if (falha) {
      setErro(falha);
      return;
    }
    notify("Portal da Azos conectado.");
    aoFechar();
  };

  return (
    <Dialog open={aberto} onOpenChange={(o) => !o && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Conexão Azos</DialogTitle>
          <DialogDescription>
            Login do portal do corretor. A senha não fica guardada; só o acesso para a leitura diária.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="azos-email">E-mail</Label>
            <Input
              id="azos-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="azos-senha">Senha</Label>
            <Input
              id="azos-senha"
              type="password"
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && email && senha && !enviando && void conectar()}
            />
          </div>
          {erro && <p className="text-sm text-destructive">{erro}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="button" onClick={conectar} disabled={enviando || !email.trim() || !senha}>
            {enviando ? "Conectando..." : "Conectar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
